<script setup>
import { computed, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  CONVERSATION_STAGE_AND_LEGACY_LABELS,
  isConversationStageLabel,
  normalizeConversationStageLabel,
} from 'dashboard/constants/conversationStages';
import { useConversationStages } from 'dashboard/composables/useConversationStages';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();
const { stageLabels } = useConversationStages();

const showLabelsDropdown = ref(false);

const currentChat = computed(() => getters.getSelectedChat.value);
const conversationId = computed(() => currentChat.value?.id);
const accountLabels = computed(
  () => getters['labels/getRegularLabels'].value || []
);
const conversationUiFlags = computed(
  () => store.getters['conversationLabels/getUIFlags']
);

const availableLabels = computed(() =>
  accountLabels.value.filter(label => !isConversationStageLabel(label))
);

const isStageTitle = label => {
  const normalizedLabel = normalizeConversationStageLabel(label);
  return (
    stageLabels.value.includes(normalizedLabel) ||
    CONVERSATION_STAGE_AND_LEGACY_LABELS.includes(label) ||
    CONVERSATION_STAGE_AND_LEGACY_LABELS.includes(normalizedLabel)
  );
};

const selectedLabels = computed(() => {
  const savedLabels = store.getters['conversationLabels/getConversationLabels'](
    conversationId.value
  );
  return savedLabels?.length ? savedLabels : currentChat.value?.labels || [];
});

const selectedRegularLabels = computed(() =>
  selectedLabels.value.filter(label => !isStageTitle(label))
);

const selectedStageLabels = computed(() =>
  selectedLabels.value.filter(isStageTitle)
);

const closeDropdown = () => {
  showLabelsDropdown.value = false;
};

const openDropdown = async () => {
  if (!accountLabels.value.length) {
    await store.dispatch('labels/get');
  }
  showLabelsDropdown.value = true;
};

const updateConversationLabels = async labels => {
  const nextLabels = [...new Set([...selectedStageLabels.value, ...labels])];
  await store.dispatch('conversationLabels/update', {
    conversationId: conversationId.value,
    labels: nextLabels,
  });
};

const addLabel = async label => {
  const nextLabels = [
    ...new Set([...selectedRegularLabels.value, label.title]),
  ];
  await updateConversationLabels(nextLabels);
  useAlert(`Etiqueta ${label.title} adicionada`);
};

const removeLabel = async title => {
  const nextLabels = selectedRegularLabels.value.filter(
    label => label !== title
  );
  await updateConversationLabels(nextLabels);
  useAlert(`Etiqueta ${title} removida`);
};

onMounted(() => {
  if (!accountLabels.value.length) {
    store.dispatch('labels/get');
  }
});
</script>

<template>
  <div class="relative flex items-center">
    <Button
      label="Etiqueta"
      icon="i-lucide-tag"
      size="sm"
      color="slate"
      no-animation
      class="flex-shrink-0 rounded-lg shadow outline-1 outline outline-n-container"
      :is-loading="conversationUiFlags.isUpdating"
      @click="openDropdown"
    />
    <div
      v-if="showLabelsDropdown"
      v-on-clickaway="closeDropdown"
      class="absolute top-full z-10 mt-1 box-border w-72 rounded-lg border border-n-strong bg-n-alpha-3 p-2 shadow-lg backdrop-blur-[100px] ltr:right-0 rtl:left-0"
    >
      <LabelDropdown
        :account-labels="availableLabels"
        :selected-labels="selectedRegularLabels"
        :allow-creation="isAdmin"
        @add="addLabel"
        @remove="removeLabel"
      />
    </div>
  </div>
</template>
